import { faChevronRight, faUser } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState } from 'react'
import { ModalCenteredUnclosable } from '../../../../../baseComponents/Modal'
import { OnSubmit } from '../../../../../forms/TwinForm/types'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { ErpLoaderLoaded } from '../types'
import { changeErrorMessage, changeUserByUserCookie, signOutUser } from '../../../../../utils/reducers/reduxDispatch'
import TwinTrans from '../../../../../baseComponents/TwinTrans'
import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import SearchBar from '../../../../../baseComponents/SearchBar'
import TwinIcon from '../../../../../baseComponents/TwinIcon'
import { appStore } from '../../../../../utils/reducers'
import { TwinTableCardSimple } from '../../../../../baseComponents/TwinTable/TwinTableCard'
import { stringMatchWithSearch } from '../../../../../utils/globals/search'
import { ButtonOrLoader } from '../../../../../baseComponents/Button'
import TwinForm from '../../../../../forms/TwinForm'
import { CustomRenderRoundedImage } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { WhiteBox } from '../../../../../baseComponents/AppLayout/WhiteBox'
import FormRenderer from '../../../../../forms/FormRenderer'
import './erpLoaderEmployeeModal.sass'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'

interface ErpLoaderEmployeeModalProps  {
    loaded: ErpLoaderLoaded
    setLoaded: React.Dispatch<React.SetStateAction<ErpLoaderLoaded>>
    loadCompany: () => Promise<void>
}

const ErpLoaderEmployeeModal: React.FC<ErpLoaderEmployeeModalProps> = ({setLoaded, loaded, loadCompany}) => {
    const { selectedEmployee, handleSelectEmployee } = useErpLoaderEmployeeModalLogic()
    return (
        <ModalCenteredUnclosable className='erp_loader_employee_modal bg-gray-F7' opened={loaded === 'employeeLeft' ? true : null}>
            <div className='flex justify-between items-center border-gray-D6 border-b py-15 px-20'>
                <h2><TwinTrans transKey='changeSeller'>Cambio de vendedor</TwinTrans></h2>
                <div className='text-green-21 cursor-pointer 'onClick={signOutUser}>
                    <TwinTrans transKey='logOut'>Cerrar sesión</TwinTrans>
                </div>
            </div>
            <div className='grid grid-cols-2 flex-auto '>
                <ListUsers employees={appStore.getState().user?.extraData?.validEmployees || []} {...{ handleSelectEmployee, selectedEmployee }} />
                <UserSelectedForm {...{ setLoaded, loadCompany, selectedEmployee}} />
            </div>
        </ModalCenteredUnclosable>
    )
 }

const useErpLoaderEmployeeModalLogic = () => {
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeModelType | null>(null)

    const handleSelectEmployee = useCallback((employee:EmployeeModelType) => {
        setSelectedEmployee(employee)
    }, [setSelectedEmployee])

    return { selectedEmployee, handleSelectEmployee, }
}

interface ListUsersProps {
    employees: EmployeeModelType[]
    selectedEmployee: EmployeeModelType | null
    handleSelectEmployee: (employee: EmployeeModelType) =>void
}

const ListUsers: React.FC<ListUsersProps> = ({ employees, handleSelectEmployee, selectedEmployee }) => {
    const {t} = useTwinTranslation()
    const { searchValue, setSearchValue } = useListUsersLogic()
    const renderThis: JSX.Element[] = []
    for (const employee of employees) {
        const isSelected = selectedEmployee?.id === employee.id
        if (stringMatchWithSearch(searchValue, employee.fullname_short)){
            renderThis.push(<EmployeeCard className={isSelected ? 'selected_employee' : ''} key={employee.id} {...{ handleSelectEmployee, employee }}>{isSelected ? <TwinIcon className='mr-15' icon={faChevronRight} /> : null}</EmployeeCard>)
        }
    }
    return (
        <div className='flex flex-auto flex-col bg-white py-20 container_list_users'>
            <SearchBar className='pl-20  pr-10 ml-10' onChange={setSearchValue} searchValue={searchValue} placeholder={t('searchNameSurname', 'Buscar por nombre y apellidos')} />
            <div className='border-gray-D6 border-b pl-20 py-10 '></div>
            <div className='flex flex-col flex-auto overflow-y-auto mt-10 h-1 pl-20 mr-10 my-10'>
                {renderThis}
            </div>
        </div>
    )
}


const useListUsersLogic = () => {
    const [searchValue, setSearchValue] = useState<string>('')
    return { searchValue, setSearchValue }
}

interface EmployeeCardProps {
    employee: EmployeeModelType
    handleSelectEmployee: (employee: EmployeeModelType) => void
    className?: string
    children?: React.ReactNode

}

const EmployeeCard: React.FC<EmployeeCardProps> = ({ employee, handleSelectEmployee, className, children}) => {
    return (
        <div className={'cursor-pointer hover:border-green-43 ' + (className || '')} onClick={()=>handleSelectEmployee(employee)}>
            <TwinTableCardSimple className='py-10 my-5 px-10' classNameImg='mr-15 h-35 w-35 min-h-35 min-w-35' icon={faUser} id={String(employee.id)} title={employee?.fullname_short} img={employee?.profile_image || '/user-png.png'}>
                {children}
            </TwinTableCardSimple>
        </div>
    )
}


interface UserSelectedFormProps {
    selectedEmployee: EmployeeModelType | null
    setLoaded: React.Dispatch<React.SetStateAction<ErpLoaderLoaded>>
    loadCompany: () => Promise<void>
}

const UserSelectedForm: React.FC<UserSelectedFormProps> = ({ selectedEmployee, setLoaded, loadCompany }) => {
    if(!selectedEmployee){
        return null
    }
    return (
        <div className='flex flex-col mx-auto my-auto w-250'>
            <EmployeeInfo name={selectedEmployee.fullname_short} img={selectedEmployee.profile_image || '/user-png.png'}/>
            <EmployeeForm {...{ selectedEmployee, setLoaded, loadCompany }} />
        </div>
    )
}

interface EmployeeInfoProps {
    name: string
    img?: string
}

const EmployeeInfo: React.FC<EmployeeInfoProps> = ({name, img}) => {
    return (
        <div className='flex flex-col justify-center items-center mb-20 gap-y-10'>
            <CustomRenderRoundedImage className='h-70 w-70 min-h-70 min-w-70 mb-5 p-3 bg-white' name={name} icon={faUser} img={img} />
            <div className='medium14'>{name}</div>
            <div><TwinTrans transKey='loginS'>Inicia sesión</TwinTrans></div>
        </div>
    )
}

interface EmployeeFormProps extends EmployeeFormLogicProps {
    selectedEmployee: EmployeeModelType
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ setLoaded, loadCompany, selectedEmployee }) => {
    const {t} = useTwinTranslation()
    const { onSubmit, onError, loading, startLoading } = useEmployeeFormLogic({ setLoaded, loadCompany })
    const sections: FormRender<EmployeeModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'code',
                    placeholder: t('code', 'Código'),
                    component: 'InputHidden',
                    value: selectedEmployee.code
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: selectedEmployee.id
                },
                {
                    name: 'name',
                    label: t('user', 'Usuario'),
                    component: 'InputWithLabelMargin',
                    value: selectedEmployee?.name + ' ' + selectedEmployee?.surname,
                    readOnly: true
                },
                {
                    name: 'password',
                    label: t('password', 'Contraseña'),
                    component: 'InputWithLabelMargin',
                    type: 'password',
                }
            ]
        }
    ]
    return (
        <WhiteBox className='p-20 text-left'>
            <TwinForm className='gap-y-20 grid' beforeSubmitHandler={() => {startLoading(); return true}} action='/api/app/employee/setEmployeeToToken' onSubmit={onSubmit} onError={onError}>
                <FormRenderer sections={sections} />
                <ButtonOrLoader className='w-full' buttonIsDisabled={loading} textButton={t('enter', 'Entrar')} loadingClassName='mr-auto'/>
            </TwinForm>
        </WhiteBox>
    )
}

interface EmployeeFormLogicProps {
    setLoaded: React.Dispatch<React.SetStateAction<ErpLoaderLoaded>>
    loadCompany: () => Promise<void>
}

const useEmployeeFormLogic = ({setLoaded, loadCompany}: EmployeeFormLogicProps) => {
    const {endLoading, loading, startLoading} = useIsLoading()
    const {t} = useTwinTranslation()
    const loginError = t('errorOnLogin', 'Error al iniciar sesión')

    const onSubmit: OnSubmit = useCallback(async (res) => {
        if (res.status === 200) {
            const token = await res.text()
            await changeUserByUserCookie({ token, type: 'erp', subType: 'temporal', changeMultiUser: false, dateLastChange: new Date().toString() })
            await loadCompany()
            setLoaded(true)
        }
        endLoading()
    }, [setLoaded, loadCompany, endLoading])

    const onError: OnSubmit = useCallback(async () => {
        changeErrorMessage(loginError)
        endLoading()
    }, [loginError, endLoading])

    return { onSubmit, onError, startLoading, loading }
}

export default ErpLoaderEmployeeModal